/**
 * Contact detail page (PRD §11.4).
 *
 * One page that renders the contact and lets the right roles edit / delete:
 *   - Sales: edit name/phone/email IF they own the contact (created or assigned).
 *   - Manager: edit all editable fields (incl. account / assignment).
 *   - Admin: same as Manager + soft-delete.
 *
 * Field-level role gates are enforced server-side; the UI mirrors them so
 * users don't see buttons they can't use. The duplicate-phone 409 is
 * handled the same way as the create form (PRD §10.1).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { Briefcase, Loader2, Pencil, Plus, Save, Trash2 } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";

import { BackLink } from "../components/BackLink";

import { ChatterPanel } from "../components/chatter/ChatterPanel";
import { CustomFieldsPanel } from "../components/custom-fields/CustomFieldsPanel";
import { DealStagePill, DealStatusPill, DealValueChip } from "../components/deals/Pills";
import { DispatchesPanel } from "../components/erp/DispatchesPanel";
import { FollowUpsPanel } from "../components/followups/FollowUpsPanel";
import { TagChips } from "../components/tags/TagChips";
import {
  ApiError,
  type Contact,
  type ContactUpdate,
  type Deal,
  accountsApi,
  contactsApi,
  dealsApi,
  isDuplicatePhoneDetail,
  usersApi,
} from "../lib/api";
import { useAuthStore } from "../lib/auth";

interface ContactDetailSearch {
  /** Optional ``?note=<id>`` deep-link from the bell / email — handed
   * straight to the chatter panel which scrolls + highlights it. */
  note?: string;
}

export const Route = createFileRoute("/_app/contacts/$contactId")({
  validateSearch: (search: Record<string, unknown>): ContactDetailSearch => {
    const n = search.note;
    return typeof n === "string" ? { note: n } : {};
  },
  component: ContactDetailPage,
});

function ContactDetailPage() {
  const { contactId } = Route.useParams();
  const { note: highlightNoteId } = useSearch({ from: "/_app/contacts/$contactId" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const [editing, setEditing] = useState(false);

  const {
    data: contact,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["contact", contactId],
    queryFn: () => contactsApi.get(contactId),
  });

  if (isLoading) {
    return (
      <Shell>
        <p className="text-sm opacity-60">Loading…</p>
      </Shell>
    );
  }

  if (error) {
    const status = error instanceof ApiError ? error.status : 0;
    return (
      <Shell>
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          {status === 404
            ? "This contact doesn't exist or you don't have access to it."
            : `Couldn't load contact: ${(error as Error).message}`}
        </div>
      </Shell>
    );
  }

  // No data yet, but neither the loading nor error branch above matched —
  // this is react-query's "paused" state (offline / flaky network on the
  // field PWA). Show a placeholder instead of a blank screen.
  if (!contact || !user) {
    return (
      <Shell>
        <p className="text-sm opacity-60">Loading…</p>
      </Shell>
    );
  }

  const owns = contact.assigned_to_id === user.id || contact.created_by_id === user.id;
  const isPrivileged = user.role === "admin" || user.role === "manager";
  const canEditBasics = isPrivileged || owns;
  const canEditAdvanced = isPrivileged;
  const canEdit = canEditBasics || canEditAdvanced;
  const canDelete = user.role === "admin";

  return (
    <Shell>
      {editing ? (
        <ContactEditor
          contact={contact}
          canEditBasics={canEditBasics}
          canEditAdvanced={canEditAdvanced}
          onSaved={(updated) => {
            queryClient.setQueryData(["contact", contactId], updated);
            queryClient.invalidateQueries({ queryKey: ["contacts"] });
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <ContactView contact={contact} canEdit={canEdit} onEdit={() => setEditing(true)} />
      )}

      <DealsForContact contact={contact} />

      <FollowUpsPanel
        parentType="contact"
        parentId={contact.id}
        defaultAssignedToId={contact.assigned_to_id}
      />

      <DispatchesPanel parentType="contact" parentId={contact.id} />

      <CustomFieldsPanel entity="contacts" recordKey={contact.id} />

      <ChatterPanel
        parentType="contact"
        parentId={contact.id}
        highlightNoteId={highlightNoteId ?? null}
      />

      {canDelete && (
        <DeleteCard
          contact={contact}
          onDeleted={() => {
            queryClient.removeQueries({ queryKey: ["contact", contactId] });
            queryClient.invalidateQueries({ queryKey: ["contacts"] });
            queryClient.invalidateQueries({ queryKey: ["contacts-trash"] });
            navigate({ to: "/contacts" });
          }}
        />
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
      <header className="space-y-1">
        <BackLink fallbackTo="/contacts" fallbackLabel="contacts" />
        <h1 className="text-2xl font-semibold tracking-tight">Contact</h1>
      </header>
      {children}
    </div>
  );
}

// ── Edit form ───────────────────────────────────────────────────────────────

interface EditorProps {
  contact: Contact;
  canEditBasics: boolean;
  canEditAdvanced: boolean;
  onSaved: (c: Contact) => void;
  onCancel: () => void;
}

function ContactEditor({
  contact,
  canEditBasics,
  canEditAdvanced,
  onSaved,
  onCancel,
}: EditorProps) {
  // Local form state mirrors the contact; we reset whenever the upstream row
  // changes so a server-side update (e.g. another tab) propagates cleanly.
  const [name, setName] = useState(contact.name);
  const [phone, setPhone] = useState(contact.phone);
  const [email, setEmail] = useState(contact.email ?? "");
  const [address, setAddress] = useState(contact.address ?? "");
  const [accountId, setAccountId] = useState(contact.account_id ?? "");
  const [assignedToId, setAssignedToId] = useState(contact.assigned_to_id ?? "");
  const [error, setError] = useState<string | null>(null);
  const [dup, setDup] = useState<{ message: string; phone: string } | null>(null);

  // Picker data — only fetched when the role can use it.
  const accountsQ = useQuery({
    queryKey: ["accounts", { limit: 200, offset: 0 }],
    queryFn: () => accountsApi.list({ limit: 200, offset: 0 }),
    enabled: canEditAdvanced,
  });
  const usersQ = useQuery({
    queryKey: ["users", { active_only: true }],
    queryFn: () => usersApi.list({ active_only: true }),
    enabled: canEditAdvanced,
  });

  useEffect(() => {
    setName(contact.name);
    setPhone(contact.phone);
    setEmail(contact.email ?? "");
    setAddress(contact.address ?? "");
    setAccountId(contact.account_id ?? "");
    setAssignedToId(contact.assigned_to_id ?? "");
  }, [contact]);

  const updateMutation = useMutation({
    mutationFn: (body: ContactUpdate) => contactsApi.update(contact.id, body),
    onSuccess: (updated) => {
      onSaved(updated);
      setError(null);
      setDup(null);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setDup(null);

    // Build a *partial* payload: only include fields the user actually changed.
    // The server uses Pydantic's `model_fields_set` to distinguish "absent"
    // from "explicit null", so omitting unchanged fields keeps the round-trip
    // semantically clean.
    const body: ContactUpdate = {};
    if (canEditBasics) {
      if (name !== contact.name) body.name = name;
      if (phone !== contact.phone) body.phone = phone;
      const emailNorm = email.trim() || null;
      if (emailNorm !== contact.email) body.email = emailNorm;
      const addressNorm = address.trim() || null;
      if (addressNorm !== contact.address) body.address = addressNorm;
    }
    if (canEditAdvanced) {
      const accountNorm = accountId.trim() || null;
      if (accountNorm !== contact.account_id) body.account_id = accountNorm;
      const assignedNorm = assignedToId.trim() || null;
      if (assignedNorm !== contact.assigned_to_id) body.assigned_to_id = assignedNorm;
    }

    if (Object.keys(body).length === 0) {
      setError("Nothing to save — no fields have changed.");
      return;
    }

    updateMutation.mutate(body, {
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409 && isDuplicatePhoneDetail(err.detail)) {
          if (err.detail.code === "duplicate_phone_visible") {
            setDup({
              message: "That phone is already used by another contact. View it instead?",
              phone: err.detail.phone,
            });
          } else {
            setDup({ message: err.detail.message, phone: err.detail.phone });
          }
          return;
        }
        const msg = err instanceof ApiError ? err.message : "Could not save changes.";
        setError(msg);
      },
    });
  };

  const submitting = updateMutation.isPending;
  const allReadOnly = !canEditBasics && !canEditAdvanced;

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-4 rounded-xl border border-[var(--border)] p-5"
      noValidate
    >
      <Field id="contact-phone" label="Phone">
        <input
          id="contact-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={phone}
          disabled={!canEditBasics}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 font-mono text-sm outline-none focus:border-[var(--accent)] disabled:opacity-60"
        />
      </Field>

      <Field id="contact-name" label="Name">
        <input
          id="contact-name"
          type="text"
          autoComplete="name"
          value={name}
          disabled={!canEditBasics}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)] disabled:opacity-60"
        />
      </Field>

      <Field id="contact-email" label="Email" hint="Optional.">
        <input
          id="contact-email"
          type="email"
          autoComplete="email"
          value={email}
          disabled={!canEditBasics}
          onChange={(e) => setEmail(e.target.value)}
          placeholder=""
          className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)] disabled:opacity-60"
        />
      </Field>

      <Field id="contact-address" label="Address" hint="Optional. Default delivery address.">
        <textarea
          id="contact-address"
          value={address}
          disabled={!canEditBasics}
          onChange={(e) => setAddress(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="Shipping / delivery address"
          className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)] disabled:opacity-60"
        />
      </Field>

      <Field
        id="contact-account"
        label="Account"
        hint={
          canEditAdvanced
            ? "Manager/Admin only. Pick an account or leave unlinked."
            : "Read-only — only managers and admins can change this."
        }
      >
        {canEditAdvanced ? (
          <select
            id="contact-account"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)] disabled:opacity-60"
          >
            <option value="">(no account)</option>
            {accountsQ.data?.items.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
            {accountId && !accountsQ.data?.items.some((a) => a.id === accountId) && (
              <option value={accountId}>(unknown — {accountId.slice(0, 8)}…)</option>
            )}
          </select>
        ) : (
          <input
            id="contact-account"
            type="text"
            value={accountId || "(no account)"}
            disabled
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm opacity-60"
          />
        )}
      </Field>

      <Field
        id="contact-assigned"
        label="Assigned to"
        hint={
          canEditAdvanced
            ? "Manager/Admin only. Pick a user or leave unassigned."
            : "Read-only — only managers and admins can change this."
        }
      >
        {canEditAdvanced ? (
          <select
            id="contact-assigned"
            value={assignedToId}
            onChange={(e) => setAssignedToId(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)] disabled:opacity-60"
          >
            <option value="">(unassigned)</option>
            {usersQ.data?.items.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email} — {u.role}
              </option>
            ))}
            {assignedToId && !usersQ.data?.items.some((u) => u.id === assignedToId) && (
              <option value={assignedToId}>(unknown — {assignedToId.slice(0, 8)}…)</option>
            )}
          </select>
        ) : (
          <input
            id="contact-assigned"
            type="text"
            value={assignedToId || "(unassigned)"}
            disabled
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm opacity-60"
          />
        )}
      </Field>

      <Meta contact={contact} />

      {dup && (
        <div
          role="alert"
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
        >
          {dup.message}
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {!allReadOnly && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="submit"
            disabled={submitting}
            className="flex items-center gap-2 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Saving…
              </>
            ) : (
              <>
                <Save className="size-4" aria-hidden />
                Save changes
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-md border border-[var(--border)] px-4 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      )}

      {allReadOnly && (
        <p className="text-xs opacity-60">You don't have permission to edit this contact.</p>
      )}
    </form>
  );
}

// ── View mode ───────────────────────────────────────────────────────────────

function ContactView({
  contact,
  canEdit,
  onEdit,
}: {
  contact: Contact;
  canEdit: boolean;
  onEdit: () => void;
}) {
  // Resolve account + assignee names. Both are TanStack-cached and shared
  // with other components on the page (e.g. the deals list, follow-ups
  // panel) so they're cheap on the toggle.
  const accountsQ = useQuery({
    queryKey: ["accounts", { limit: 200, offset: 0 }],
    queryFn: () => accountsApi.list({ limit: 200, offset: 0 }),
  });
  const usersQ = useQuery({
    queryKey: ["users", { active_only: true }],
    queryFn: () => usersApi.list({ active_only: true }),
  });

  const accountName =
    contact.account_id && accountsQ.data?.items.find((a) => a.id === contact.account_id)?.name;
  const assignee =
    contact.assigned_to_id && usersQ.data?.items.find((u) => u.id === contact.assigned_to_id);

  return (
    <section className="space-y-5 rounded-xl border border-[var(--border)] p-5">
      <div>
        <h2 className="text-xl font-semibold">{contact.name}</h2>
        <p className="font-mono text-sm opacity-70">{contact.phone}</p>
        {contact.email && <p className="text-sm opacity-70">{contact.email}</p>}
      </div>

      <div>
        <div className="mb-1.5 text-xs uppercase tracking-wide opacity-50">Tags</div>
        <TagChips parentType="contact" parentId={contact.id} />
      </div>

      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
        <ViewRow
          label="Account"
          value={
            accountName ??
            (contact.account_id ? (
              <span className="font-mono text-xs">{contact.account_id.slice(0, 8)}…</span>
            ) : (
              <em className="opacity-50">(no account)</em>
            ))
          }
        />
        <ViewRow
          label="Assigned to"
          value={
            assignee ? (
              <span>
                {assignee.name || assignee.email}{" "}
                <span className="text-xs opacity-60">— {assignee.role}</span>
              </span>
            ) : contact.assigned_to_id ? (
              <span className="font-mono text-xs">{contact.assigned_to_id.slice(0, 8)}…</span>
            ) : (
              <em className="opacity-50">(unassigned)</em>
            )
          }
        />
      </dl>

      <Meta contact={contact} />

      {canEdit ? (
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-2 rounded-md border border-[var(--border)] px-4 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5"
        >
          <Pencil className="size-4" aria-hidden />
          Edit
        </button>
      ) : (
        <p className="text-xs opacity-60">You don't have permission to edit this contact.</p>
      )}
    </section>
  );
}

function ViewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wide opacity-50">{label}</dt>
      <dd className="mt-0.5 truncate">{value}</dd>
    </div>
  );
}

// ── Soft-delete (Admin only) ────────────────────────────────────────────────

function DeleteCard({ contact, onDeleted }: { contact: Contact; onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () => contactsApi.remove(contact.id),
    onSuccess: () => onDeleted(),
    onError: (err) => {
      // Surface the re-auth requirement specifically (PRD §6.2 sensitive-op
      // gate). The current backend only renews the gate on fresh login, so
      // the recovery path is "log out and back in".
      if (err instanceof ApiError && err.reauthRequired) {
        setError("For security, please log out and log back in to confirm this delete.");
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not delete contact.";
      setError(msg);
    },
  });

  return (
    <div className="space-y-3 rounded-xl border border-red-300/60 p-5 dark:border-red-900/60">
      <div className="flex items-start gap-3">
        <Trash2 className="mt-0.5 size-4 text-red-600 dark:text-red-400" aria-hidden />
        <div>
          <h2 className="text-sm font-semibold text-red-700 dark:text-red-300">Danger zone</h2>
          <p className="text-xs opacity-70">
            Soft-deleting hides this contact from all lists. The phone number becomes available
            again. Admin can restore from <em>Trash</em>.
          </p>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {!confirming ? (
        <button
          type="button"
          onClick={() => {
            setError(null);
            setConfirming(true);
          }}
          className="rounded-md border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/40"
        >
          Move to trash…
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm">Move "{contact.name}" to trash?</span>
          <button
            type="button"
            disabled={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate()}
            className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {deleteMutation.isPending ? "Deleting…" : "Confirm delete"}
          </button>
          <button
            type="button"
            disabled={deleteMutation.isPending}
            onClick={() => setConfirming(false)}
            className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

// ── Deals on this contact (PRD §11.4) ───────────────────────────────────────

/**
 * Inline deals list for the contact detail page.
 *
 * Shows up to 50 deals attached to this contact. The "New deal" button
 * deep-links to /deals/new?contact=<id> so the contact is pre-selected.
 *
 * Empty state nudges the user to create one. We deliberately do NOT hide
 * this section for contacts the caller can see but has no role to create
 * deals — Sales can always create their own deals.
 */
function DealsForContact({ contact }: { contact: Contact }) {
  const me = useAuthStore((s) => s.user);
  const isPrivileged = me?.role === "admin" || me?.role === "manager";

  const { data, error, isLoading } = useQuery({
    queryKey: ["deals", { contact_id: contact.id, limit: 50 }],
    queryFn: () => dealsApi.list({ contact_id: contact.id, limit: 50 }),
  });

  // Sales can't list users (403), so we only fetch for Manager+. Sales
  // reps still get a useful "You" label whenever the assignee is them;
  // any other id falls back to a short id chip.
  const usersQ = useQuery({
    queryKey: ["users", { active_only: true }],
    queryFn: () => usersApi.list({ active_only: true }),
    enabled: isPrivileged,
  });
  const usersById = new Map((usersQ.data?.items ?? []).map((u) => [u.id, u]));

  return (
    <section className="space-y-3 rounded-xl border border-[var(--border)] p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Briefcase className="size-4 opacity-70" aria-hidden />
          <h2 className="text-sm font-semibold">Deals</h2>
          {data && (
            <span className="rounded-full bg-black/5 px-2 py-0.5 text-xs opacity-70 dark:bg-white/10">
              {data.total}
            </span>
          )}
        </div>
        <Link
          to="/deals/new"
          search={{ contact: contact.id }}
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-2.5 py-1.5 text-xs font-medium hover:bg-black/5 dark:hover:bg-white/5"
        >
          <Plus className="size-3.5" aria-hidden />
          New deal
        </Link>
      </div>

      {isLoading ? (
        <p className="text-xs opacity-60">Loading…</p>
      ) : error ? (
        <p className="text-xs text-red-700 dark:text-red-300">
          Couldn't load deals: {(error as Error).message}
        </p>
      ) : !data || data.items.length === 0 ? (
        <p className="text-xs opacity-60">No deals yet for this contact.</p>
      ) : (
        <ul className="divide-y divide-[var(--border)] overflow-hidden rounded-md border border-[var(--border)]">
          {data.items.map((deal) => {
            const assignee = deal.assigned_to_id
              ? (usersById.get(deal.assigned_to_id) ?? null)
              : null;
            const assigneeLabel = (() => {
              if (!deal.assigned_to_id) return "Unassigned";
              if (deal.assigned_to_id === me?.id) return "You";
              if (assignee) return assignee.name || assignee.email.split("@")[0] || assignee.email;
              return `${deal.assigned_to_id.slice(0, 8)}…`;
            })();
            return <DealRow key={deal.id} deal={deal} assigneeLabel={assigneeLabel} />;
          })}
        </ul>
      )}
    </section>
  );
}

function DealRow({ deal, assigneeLabel }: { deal: Deal; assigneeLabel: string }) {
  return (
    <li>
      <Link
        to="/deals/$dealId"
        params={{ dealId: deal.id }}
        className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 text-sm transition-colors hover:bg-black/5 dark:hover:bg-white/5"
      >
        <div className="min-w-0 space-y-0.5">
          <div className="text-xs opacity-80">
            Assigned to <span className="font-medium opacity-100">{assigneeLabel}</span>
          </div>
          <div className="text-xs opacity-60">
            Created {new Date(deal.created_at).toLocaleDateString()}
            {deal.closed_at && (
              <>
                {" · Closed "}
                {new Date(deal.closed_at).toLocaleDateString()}
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <DealStagePill stage={deal.stage} />
          <DealStatusPill status={deal.status} />
          <DealValueChip value={deal.value} />
        </div>
      </Link>
    </li>
  );
}

// ── Small bits ──────────────────────────────────────────────────────────────

function Meta({ contact }: { contact: Contact }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 border-t border-[var(--border)] pt-3 text-xs opacity-60">
      <dt>Created</dt>
      <dd>{new Date(contact.created_at).toLocaleString()}</dd>
      <dt>Updated</dt>
      <dd>{new Date(contact.updated_at).toLocaleString()}</dd>
    </dl>
  );
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="text-sm">
      <label htmlFor={id} className="mb-1 block opacity-80">
        {label}
      </label>
      {children}
      {hint && <span className="mt-1 block text-xs opacity-50">{hint}</span>}
    </div>
  );
}
